import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { EmployeeWithAvatar } from '../../../components/Employee';
import { EmployeeInfoSidebar } from '../../../components/EmployeeInfoSidebar';
import GlobalTableFilter from '../../../components/GlobalTableFilter';
import PageHeader from '../../../components/PageHeader';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { TableContent } from '../../../manager/components/TableContent';
import { useDirectoryQuery } from '../../../__generated__/graphql';
import DirectoryFilters from './DirectoryFilters';
import { filterFromQuery } from './DirectoryFilters/logic';
import { mapEmployees } from './helpers';
import { EmployeeRow } from './types';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsTableId,
  ExtraTableRenderSettings,
  ExtraTableItemType,
} from '../../../components/ExtraFields/types';
import { useExtraTableColumns } from '../../../components/ExtraFields/useExtraTableColumns';
import { useObjectLabels } from '../../../hooks/useObjectLabels';
import {
  mapToExtraFieldInternalValue,
  toDisplayValue,
} from '../../../components/ExtraFields/extraFieldInternalValue';

const i18Path = 'people.directory';

const Content: React.FC = () => {
  const t = useNamespacedTranslation(i18Path);
  const objectLabels = useObjectLabels();
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeRow | null>(
    null,
  );
  const [allEmployees, setAllEmployees] = useState<
    ExtraTableItemType<EmployeeRow>[]
  >([]);
  const [employees, setEmployees] = useState<ExtraTableItemType<EmployeeRow>[]>(
    [],
  );
  const location = useLocation();
  const queryParams: URLSearchParams = new URLSearchParams(location.search);
  const filter = filterFromQuery(queryParams);
  const employeeIdParam = queryParams.get('employeeId');
  const { data, loading } = useDirectoryQuery({
    variables: {
      locations: filter.locations,
      teams: filter.teams,
      departments: filter.departments,
    },
  });

  useEffect(() => {
    const employees = mapEmployees(data?.activeEmployees);
    const enrichedEmployees: ExtraTableItemType<EmployeeRow>[] = employees.map(
      (employee) => {
        const extraFieldData = employee.extraFields.reduce(
          (acc, extraField) => {
            const displayValue = toDisplayValue(
              mapToExtraFieldInternalValue(extraField),
            );
            acc[extraField.fieldApiName] = displayValue;
            acc.rawExtraFields[extraField.fieldApiName] = extraField;
            return acc;
          },
          { rawExtraFields: {} } as ExtraTableItemType<EmployeeRow>,
        );
        return {
          ...extraFieldData,
          ...employee,
        };
      },
    );
    setEmployees(enrichedEmployees);
    setAllEmployees(enrichedEmployees);

    if (employeeIdParam) {
      const match = enrichedEmployees.find((e) => e.id === employeeIdParam);
      if (match) {
        setSelectedEmployee(match);
      }
    }
  }, [data, employeeIdParam]);

  const onSearch = useCallback(
    (searchText: string) => {
      if (searchText) {
        setEmployees(
          allEmployees.filter((e) =>
            e.name.toLowerCase().includes(searchText.toLowerCase()),
          ),
        );
      } else {
        setEmployees(allEmployees);
      }
    },
    [allEmployees],
  );

  const renderSettings: ExtraTableRenderSettings<EmployeeRow> = useMemo(
    () => ({
      beforePersistentColumns: {
        name: {
          Header: objectLabels.employee,
          Cell: (props) => (
            <EmployeeWithAvatar
              employee={{
                name: props.value,
                avatarUrl: props.row.original.avatarUrl,
              }}
            />
          ),
        },
      },
      columns: {
        flair__Company_Email__c: {
          Cell: (props) => {
            const email = props.value;
            if (!email || email === '-') {
              return <>{email}</>;
            }
            return <a href={`mailto:${email}`}>{email}</a>;
          },
        },
      },
    }),
    [objectLabels.employee],
  );

  const { columns, loading: extraFieldsLoading } =
    useExtraTableColumns<EmployeeRow>({
      objectApiName: ExtraFieldsSupportedObject.Employee,
      renderSettings,
      tableId: ExtraFieldsTableId.Directory,
    });

  const onCloseSidebar = () => setSelectedEmployee(null);

  return (
    <Card>
      <Card.Header>
        <div className="d-flex align-items-center small">
          <GlobalTableFilter
            placeholder={t('search')}
            onFilterChange={onSearch}
          />
          <div className="me-auto">
            <DirectoryFilters />
          </div>
        </div>
      </Card.Header>

      {loading || extraFieldsLoading ? (
        <SkeletonTable className="card-table" columns={4} rows={15} />
      ) : (
        <>
          <TableContent
            columns={columns}
            data={employees}
            onRowClick={setSelectedEmployee}
            pageSize={10}
            initialSortBy={[{ id: 'name' }]}
          />
          <EmployeeInfoSidebar
            show={!!selectedEmployee}
            onClose={onCloseSidebar}
            employee={selectedEmployee}
          />
        </>
      )}
    </Card>
  );
};

const i18PathDirectory = 'navigation.menuItems.company';

const Directory: React.FC = () => {
  const t = useNamespacedTranslation(i18PathDirectory);

  useFlairBreadcrumbHook([
    { label: t('title') },
    {
      label: t('subMenuItems.directory.title'),
    },
  ]);

  return (
    <>
      <PageHeader title={t('subMenuItems.directory.title')} />
      <Content />
    </>
  );
};

export default Directory;
